import { useState } from 'react';
import type { PlannerDayDto, PlannerPickDto } from '@fewerbags/schema';
import { costLine } from '../../lib/autoplan';
import { formatDayLong } from '../../lib/planDates';
import { formatMinutes } from '../../lib/planner';
import { reasonChipClass, SLOT_LABEL } from '../../lib/plans';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { chipButton } from './AttendeePicker';

export type SwapReason = 'didnt_want' | 'other';

/**
 * "Swap" for one proposed meal (D33): the planner's next candidates with
 * their reasons, cost and warnings, or a blind re-roll. The reason decides
 * whether the swap teaches the planner ("Didn't want it" lowers the recipe's
 * ingredients for the people eating; "Just something else" records nothing).
 */
export function PlannerSwapSheet({
  open,
  day,
  onClose,
  onChoose,
  onReroll,
}: {
  open: boolean;
  day: PlannerDayDto;
  onClose: () => void;
  onChoose: (pick: PlannerPickDto, reason: SwapReason) => void;
  onReroll: (reason: SwapReason) => void;
}) {
  const [reason, setReason] = useState<SwapReason>('other');
  const current = day.main?.recipe.title ?? 'this meal';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Swap ${SLOT_LABEL[day.slot].toLowerCase()}, ${formatDayLong(day.date)}`}
    >
      <p className="-mt-2 text-sm text-muted">Instead of {current}</p>

      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Why swap?">
        <button
          type="button"
          role="radio"
          aria-checked={reason === 'other'}
          className={chipButton(reason === 'other')}
          onClick={() => setReason('other')}
        >
          Just something else
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={reason === 'didnt_want'}
          className={chipButton(reason === 'didnt_want')}
          onClick={() => setReason('didnt_want')}
        >
          Didn&apos;t want it
        </button>
      </div>
      {reason === 'didnt_want' ? (
        <p className="-mt-2 text-xs text-muted">
          The planner will suggest its ingredients a little less for the people eating.
        </p>
      ) : null}

      {day.alternatives.length === 0 ? (
        <p className="text-sm text-muted">No other recipe fits this meal. Try a re-roll.</p>
      ) : (
        <ul className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto" aria-label="Alternatives">
          {day.alternatives.map((alt) => {
            const chips = [...alt.reasons]
              .filter((r) => r.kind !== 'window')
              .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
              .slice(0, 3);
            const cost = costLine(alt.cost);
            const time = formatMinutes(alt.recipe.totalMin);
            return (
              <li
                key={alt.recipe.recipeId}
                className="flex flex-col gap-1.5 rounded-xl border border-line p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="leading-snug font-semibold">{alt.recipe.title}</p>
                    <p className="text-xs text-muted">
                      {[time, cost, alt.recipe.own ? null : 'Shared'].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    className={chipButton(false)}
                    onClick={() => onChoose(alt, reason)}
                    aria-label={`Choose ${alt.recipe.title}`}
                  >
                    Choose
                  </button>
                </div>
                {chips.length > 0 ? (
                  <ul className="flex flex-wrap gap-1">
                    {chips.map((r, i) => (
                      <li
                        key={`${r.kind}-${i}`}
                        className={`rounded-full px-2 py-0.5 text-xs ${reasonChipClass(r.delta)}`}
                      >
                        {r.text}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {alt.warnings.map((w, i) => (
                  <p
                    key={`${w.kind}-${i}`}
                    className="rounded-lg bg-orange-soft px-2.5 py-1 text-xs font-medium text-orange-ink"
                  >
                    {w.text}
                  </p>
                ))}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="secondary" onClick={() => onReroll(reason)}>
          Re-roll
        </Button>
      </div>
    </Dialog>
  );
}
